import { useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';

import { handleAxiosErrorMessage } from '../../../../utils';
import {
  isStageAlreadyExistsError,
  useLeadEventsCache,
} from '../../accountCreation';
import { LEAD_EVENT_STAGES } from '../../constants/leadEventStages';
import { INVALID_IDENTITY_NUMBER } from '../utils/registration.constants';
import { mapVehicleTypeToApi } from '../mappers';
import { IS_BR } from '../utils/registrationConfig';
import {
  normalizeCpfForApi,
  normalizeRutForApi,
} from '../utils/identityDocument';

const RETRIABLE_ERROR_CODES = new Set([
  'GENERAL_ERROR',
  'NETWORK_ERROR',
  'TIMEOUT_ERROR',
]);
const PERSONAL_DETAILS_MAX_RETRIES = 2;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const normalizeCnpjForApi = (value) => {
  if (!value || typeof value !== 'string') return '';
  return value.replace(/\D/g, '');
};

const hasLeadStage = (events, stage) =>
  Array.isArray(events) && events.some((event) => event?.stage === stage);

export function useRegistrationSubmit({
  t,
  navigate,
  leadEvents,
  clearErrors,
  setError,
  validateIdentityMutation,
  postLeadEventMutation,
  requestExternalValidationMutation,
}) {
  const { getCachedLeadEvents, refetchLeadEvents } = useLeadEventsCache();
  const dismissSuccessModalLock = useRef(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const isSubmitting =
    validateIdentityMutation.isPending ||
    postLeadEventMutation.isPending ||
    requestExternalValidationMutation.isPending;

  const hasPersonalDetailsStage = useMemo(
    () => hasLeadStage(leadEvents, LEAD_EVENT_STAGES.PERSONAL_DETAILS),
    [leadEvents]
  );

  const submitLeadStage = async (payload) => {
    try {
      await postLeadEventMutation.mutateAsync(payload);
    } catch (error) {
      if (isStageAlreadyExistsError(error)) return;
      throw error;
    }
  };

  const submitLeadStageWithRetry = async (payload, maxRetries) => {
    for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
      try {
        await submitLeadStage(payload);
        return;
      } catch (error) {
        const errorCode = handleAxiosErrorMessage(error);
        const shouldRetry =
          RETRIABLE_ERROR_CODES.has(errorCode) && attempt < maxRetries;
        if (!shouldRetry) throw error;
        await wait((attempt + 1) * 400);
      }
    }
  };

  const getLeadEventsFromCache = () => {
    const cached = getCachedLeadEvents();
    return cached.length ? cached : leadEvents;
  };

  const refreshLeadEvents = async () => {
    await refetchLeadEvents();
    return getLeadEventsFromCache();
  };

  const onSubmit = async (values) => {
    setSubmitError(null);
    const identityField = IS_BR ? 'cpf' : 'rut';
    clearErrors(identityField);
    let hasPersonalDetails = hasLeadStage(
      getLeadEventsFromCache(),
      LEAD_EVENT_STAGES.PERSONAL_DETAILS
    );

    if (!hasPersonalDetails) {
      try {
        await validateIdentityMutation.mutateAsync({
          rawValue: values[identityField],
        });
      } catch (error) {
        if (handleAxiosErrorMessage(error) === INVALID_IDENTITY_NUMBER) {
          const latestEvents = await refreshLeadEvents();
          hasPersonalDetails = hasLeadStage(
            latestEvents,
            LEAD_EVENT_STAGES.PERSONAL_DETAILS
          );
          if (!hasPersonalDetails) {
            setError(identityField, {
              type: 'server',
              message: t('registration.page.errors.identity_number_taken'),
            });
            return;
          }
        } else {
          setSubmitError(t('registration.page.errors.submit_failed'));
          return;
        }
      }
    }

    try {
      const identityNumber = IS_BR
        ? normalizeCpfForApi(values.cpf)
        : normalizeRutForApi(values.rut);

      const personalDetailsContent = {
        identity_number: identityNumber,
        address: values.address,
        birth_date: dayjs(values.birth_date).format('YYYY-MM-DD'),
      };

      if (IS_BR) {
        personalDetailsContent.address_detail = values.neighborhood;
        const cnpj = normalizeCnpjForApi(values.cnpj);
        if (cnpj) {
          personalDetailsContent.cnpj = cnpj;
        }
      }

      if (!hasPersonalDetails) {
        await submitLeadStageWithRetry(
          {
            stage: LEAD_EVENT_STAGES.PERSONAL_DETAILS,
            content: personalDetailsContent,
          },
          PERSONAL_DETAILS_MAX_RETRIES
        );
      }

      await submitLeadStage({
        stage: LEAD_EVENT_STAGES.VEHICLE_INFO,
        content: {
          vehicle_type: mapVehicleTypeToApi(values.vehicle_type),
        },
      });
    } catch {
      setSubmitError(t('registration.page.errors.submit_failed'));
      return;
    }

    try {
      await requestExternalValidationMutation.mutateAsync();
      setShowSuccessModal(true);
    } catch {
      setSubmitError(t('registration.page.errors.submit_failed'));
    }
  };

  const handleDismissSuccessModal = async () => {
    if (dismissSuccessModalLock.current) return;
    dismissSuccessModalLock.current = true;
    setShowSuccessModal(false);
    await refreshLeadEvents();
    navigate('/onboarding/pending');
  };

  return {
    onSubmit,
    isSubmitting,
    hasPersonalDetailsStage,
    showSuccessModal,
    handleDismissSuccessModal,
    submitError,
  };
}
